import type { ActionFunctionArgs } from "react-router";
import { useActionData, Form, Link } from "react-router";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Button } from "~/components/shared/Button";
import { InputField, SearchableSelectField, SelectField } from "~/components/shared/Form";
import locations from "~/helpers/locations";
import signup from "~/helpers/signup";
import { createSession, login } from "~/session.server";
import { deltaDate } from "~/utils/date-delta";

export const action = async ({ request }: ActionFunctionArgs) => {
  if (request.method === "POST") {
    const formData = await request.formData();
    const result = await signup(formData);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    if (!result.errors) {
      const loginResult = await login({ email, password });
      if (loginResult.accessToken) {
        const { accessToken } = loginResult;
        return createSession({ accessToken, redirectTo: "/profile" });
      }
    }
    return result.errors;
  }
};

export function meta() {
  return [{
    title: "Signup - Jiwe eSports",
  }];
}

export default function RegisterRoute() {
  const signupError = useActionData<string>();

  useEffect(() => {
    if (signupError) {
      toast.error(signupError);
    }
  }, [signupError]);

  return (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-center sm:items-center sm:content-center mt-[2rem] mb-12">
      <div className="mx-[10%]">
        <div className="flex flex-col items-center mb-8 pb-6 border-b-[.5px] border-b-divider dark:border-b-divider-dark">
          <img src="/assets/logo.png" alt="logo" className="h-[4rem] mb-5" />
          <h1 className="font-bold text-xl text-center text-grey-border uppercase ml-[1rem]">
            Create your <br />
            <span className="text-jiwe-blue dark:text-jiwe-red">
              Jiwe esports
            </span>
            <br />
            account
          </h1>
        </div>
        <Form method="post">
          <InputField type="text" name="name" label="Name" required />
          <InputField
            placeholder="Your display name"
            type="text"
            name="nickname"
            label="Display name"
            required
          />
          <InputField
            placeholder="name@email.com"
            type="email"
            name="email"
            label="Email"
            required
          />
          <InputField
            placeholder="+254700123456"
            type="text"
            name="phoneNumber"
            label="Phone Number"
            required
          />
          <InputField
            type="date"
            name="dateOfBirth"
            label="Date of birth"
            max={
              deltaDate(new Date(), { years: -18 }).toISOString().split("T")[0]
            }
            required
          />
          <SelectField
            name="gender"
            label="Gender"
            options={[
              { name: "Female", value: "female" },
              { name: "Male", value: "male" },
            ]}
            required
          />
          <SearchableSelectField
            name="location"
            label="Location"
            options={locations}
            required
          />
          <InputField
            placeholder="6+ Characters"
            type="password"
            name="password"
            label="Password"
            required
          />
          <SelectField
            name="preferredGames"
            label="Preferred Games"
            options={[
              { name: "Call of Duty Mobile", value: "Call of Duty Mobile" },
              { name: "Call of Duty Warzone", value: "Call of Duty Warzone" },
              { name: "PUBG Mobile", value: "PUBG" },
              { name: "Tekken", value: "Tekken" },
              { name: "Mortal Kombat", value: "Mortal Kombat" },
              { name: "FIFA", value: "FIFA" },
              { name: "FreeFire", value: "FreeFire" },
              { name: "Fortnite", value: "Fortnite" },
              { name: "Rocket League", value: "RocketLeague" },
            ]}
            required
            multiple
          />
          <div className="flex gap-x-4 mt-8 dark:text-grey-light">
            <input type="checkbox" id="acceptedTOCs" name="acceptedTOCs" />
            <label htmlFor="acceptedTOCs" className="text-sm">
              I accept the{" "}
              <Link to="/terms-and-conditions" className="text-jiwe-blue font-bold">
                Terms of Service
              </Link>{" "}
              and I acknowledge having read the{" "}
              <Link to="/privacy-policy" className="text-jiwe-blue font-bold">
                Privacy Policy
              </Link>
              .
            </label>
          </div>
          <Button
            type="submit"
            className="bg-jiwe-yellow uppercase w-full mb-8"
          >
            Create Account
          </Button>
          <div className="mt-4 block text-center font-bold text-jiwe-red dark:text-jiwe-blue">
            <Link to="/login">I already have an account</Link>
          </div>
        </Form>
      </div>
    </div>
  );
}
